import React, { useState } from 'react'
import { ShareIcon, XIcon } from 'lucide-react'
import { createMessageShare } from '../../api/messageShares'
import type { InboxAssignee } from '../../types/inbox'
import { assigneeDisplayName } from '../../lib/inboxPermissions'

interface ShareMessagesModalProps {
  isOpen: boolean
  conversationId: string
  messageIds: string[]
  assignees: InboxAssignee[]
  onClose: () => void
  onShared?: () => void
}

export function ShareMessagesModal({
  isOpen,
  conversationId,
  messageIds,
  assignees,
  onClose,
  onShared,
}: ShareMessagesModalProps) {
  const [recipientId, setRecipientId] = useState('')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen) return null

  async function handleShare() {
    if (!recipientId) {
      setError('Select a recipient')
      return
    }
    setLoading(true)
    setError('')
    try {
      await createMessageShare({
        conversationId,
        messageIds,
        sharedWithUserId: recipientId,
        note: note.trim() || null,
      })
      onShared?.()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Share failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ShareIcon className="w-5 h-5 text-gray-500" />
            Share selected messages
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Field staff receive a read-only packet of {messageIds.length} message
            {messageIds.length === 1 ? '' : 's'} — not full thread access.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Share with</label>
            <select
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select team member…</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>
                  {assigneeDisplayName(u)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={loading}
              onClick={handleShare}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Sharing…' : 'Share packet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
